import type { ApplicationStatus } from '@core/enums';

// ─────────────────────────────────────────────
// Auth & Org Types
// ─────────────────────────────────────────────

export type OrgRole =
  | 'owner'
  | 'admin'
  | 'senior_recruiter'
  | 'junior_recruiter'
  | 'viewer';

export type OrgPlan = 'starter' | 'team' | 'agency' | 'enterprise';

export interface AuthUser {
  id: string;          // Keycloak sub
  email: string;
  name: string;
  orgId: string | null;
  orgName: string | null;
  orgUserId: string | null;  // ggj_b2b_org_users.id (UUID) — used for FK references
  roles: OrgRole[];
  credits?: number;    // org.credits_balance for display
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: OrgPlan;
  seats_allocated: number;
  credits_balance: number;
  /** INR owed to platform (subscription/invoices); offboarding blocked while > 0 when enforced in UI. */
  billing_outstanding_inr?: number;
  billing_email: string | null;
  gst_number: string | null;
  created_at: string;
  /** PUB-020 — persisted onboarding checklist (default false if column missing on old DB). */
  onboarding_smtp_completed?: boolean | null;
  onboarding_team_completed?: boolean | null;
  onboarding_pipeline_completed?: boolean | null;
  onboarding_workflows_completed?: boolean | null;
  /** ORG-030 — data retention & compliance (migration 023). */
  retention_days?: number | null;
  consent_version?: string | null;
  gdpr_dpa_signed?: boolean | null;
  gdpr_dpa_signed_at?: string | null;
  /** APP-020: Kanban column order (application status keys); JSON array in DB. */
  pipeline_stage_order?: ApplicationStatus[] | null;
  /** Phase 8 · C-38 — org-level default TAT for new positions (1–365, default 30). */
  default_tat_days?: number | null;
  /**
   * Phase 8 · C-47 — free-form jsonb settings bucket on the org row.
   * Known keys:
   *   open_profiles (bool) — "Allow team members to view each other's profiles".
   */
  settings?: Record<string, unknown> | null;
}

export interface OrgUser {
  id: string;
  org_id: string;
  keycloak_sub: string;
  display_name: string;
  email: string;
  role: OrgRole;
  is_active: boolean;
  invited_at: string | null;
  joined_at: string | null;
  /** Storage path in B2B bucket (same as candidate avatars). */
  avatar_url?: string | null;
  /** Self-service contact & profile (migration 067). */
  phone?: string | null;
  linkedin_url?: string | null;
  profile_notes?: string | null;
  education?: string | null;
  /** Storage path for uploaded résumé (PDF/DOC). */
  resume_url?: string | null;
  /**
   * Phase 8 · C-47 — self-reference to the org_user this member rolls
   * up to. Feeds `ggj_b2b_org_user_hierarchy` (migration 111). NULL =
   * top of the tree / independent IC / pending invite.
   */
  manager_id?: string | null;
}

// ─────────────────────────────────────────────
// Candidate Types
// ─────────────────────────────────────────────

export const SOURCE_CHANNELS = [
  'LinkedIn', 'Indeed', 'Naukri', 'Referral', 'Job Board',
  'Direct', 'Walk-in', 'Agency', 'College / Campus', 'Other',
] as const;

export type SourceChannel = (typeof SOURCE_CHANNELS)[number];

export type CandidateSource =
  | 'manual' | 'linkedin' | 'referral' | 'job_board'
  | 'career_site' | 'bulk_upload' | 'api' | 'other';

export const CANDIDATE_SOURCE_LABELS: Record<CandidateSource, string> = {
  manual:      'Manual entry',
  linkedin:    'LinkedIn',
  referral:    'Referral',
  job_board:   'Job board',
  career_site: 'Career site',
  bulk_upload: 'Bulk upload',
  api:         'API / Integration',
  other:       'Other',
};

export interface Candidate {
  id: string;
  org_id: string;
  created_by: string;
  full_name: string;
  email: string;
  phone: string | null;
  linkedin_url: string | null;
  source_channel: SourceChannel | string | null;
  /** CAN-050: structured source enum stored in DB */
  source: CandidateSource | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  current_company: string | null;
  current_title: string | null;
  location: string | null;
  /** Storage path for profile photo (same bucket as résumés). Omitted until DB column is migrated. */
  avatar_url?: string | null;
  base_resume_url: string | null;
  /** Internal 1–5 recruiter rating; null if unset */
  recruiter_rating: number | null;
  parsed_skills: string[] | null;
  /** §10.4 — async AI queue status (polled on candidate detail). */
  async_ai_work_status?: 'idle' | 'queued' | 'processing' | 'done' | 'failed' | null;
  async_ai_work_operation?: string | null;
  async_ai_work_error?: string | null;
  async_ai_work_updated_at?: string | null;
  years_exp: number | null;
  total_experience_years?:  number | null;
  current_ctc?:             number | null;
  expected_ctc?:            number | null;
  notice_period_days?:      number | null;
  preferred_locations?:     string[] | null;
  profile_structured_json?: Record<string, unknown> | null;
  resume_quality_score?:    number | null;
  pinecone_vector_id?:      string | null;
  education: EducationEntry[] | null;
  tags: string[];
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EducationEntry {
  degree: string;
  institution: string;
  year: number | null;
}

// ─────────────────────────────────────────────
// Client (Company) Types
// ─────────────────────────────────────────────

export type ClientOnboardingStatus = 'onboarding' | 'ready_to_recruit';

export type ClientComplianceDocStatus = 'pending_review' | 'approved' | 'rejected';

/** CLI-051: uploaded compliance file for a hiring-company client; path lives in ggj_b2b-attachments (see storageConfig). */
export interface ClientComplianceDocument {
  id: string;
  org_id: string;
  client_id: string;
  doc_type: string;
  storage_path: string;
  file_name: string;
  status: ClientComplianceDocStatus;
  uploaded_by: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  review_note: string | null;
  created_at: string;
  updated_at: string;
}

export interface Client {
  id: string;
  org_id: string;
  name: string;
  domain: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  industry: string | null;
  default_masking_template_id: string | null;
  notes: string | null;
  /** CLI-050 — agreement / MOU / contract timeline (hiring company commercial relationship). */
  commercial_agreement_label?: string | null;
  mou_reference?: string | null;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
  commercial_terms_summary?: string | null;
  /** Optional AR balance for this hiring company (INR); surfaced in tenant offboarding when > 0. */
  ar_balance_inr?: number;
  /** CLI-051 — consent + compliance gate before reqs/jobs. */
  client_onboarding_status?: ClientOnboardingStatus | null;
  client_consent_accepted_at?: string | null;
  client_consent_version?: string | null;
  onboarding_verified_at?: string | null;
  onboarding_verified_by?: string | null;
  compliance_pending_request_note?: string | null;
  created_at: string;
}

// ─────────────────────────────────────────────
// Job / JD Types
// ─────────────────────────────────────────────

export type { JobStatus } from '@core/enums';

export type JobPublishStatus = 'draft' | 'pending_review' | 'scheduled' | 'published' | 'paused' | 'closed';

export const JOB_PUBLISH_STATUS_LABELS: Record<JobPublishStatus, string> = {
  draft:          'Draft',
  pending_review: 'Pending review',
  scheduled:      'Scheduled',
  published:      'Published',
  paused:         'Paused',
  closed:         'Closed',
};

export interface Job {
  id: string;
  org_id: string;
  client_id: string;
  title: string;
  description: string | null;
  jd_url: string | null;
  skills_required: string[];
  experience_min: number | null;
  experience_max: number | null;
  location: string | null;
  /** Work arrangement (JD extraction, forms); DB column added in migration 084 */
  employment_type?: string | null;
  status: JobStatus;
  /** JOB-010: publish lifecycle */
  publish_status: JobPublishStatus | null;
  published_at: string | null;
  publish_channels: string[];
  /** JOB-011: scheduled publish */
  scheduled_publish_at: string | null;
  scheduled_publish_job_id?: string | null;
  /** §9.14: scheduled unpublish (take job off live listings) */
  scheduled_unpublish_at?: string | null;
  scheduled_unpublish_job_id?: string | null;
  /** PRD §11.1: short public blurb for career listings / SEO */
  career_seo_summary?: string | null;
  opened_at: string | null;
  filled_at: string | null;
  created_by: string;
  created_at: string;
  hiring_deadline?:      string | null;
  budget_min?:           number | null;
  budget_max?:           number | null;
  budget_currency?:      string;
  openings_count?:       number;
  openings_filled?:      number;
  jd_source_file?:       string | null;
  jd_structured_json?:   Record<string, unknown> | null;
  urgency_score?:        number | null;
  pinecone_vector_id?:   string | null;
  // joined
  client?: Client;
}

// ─────────────────────────────────────────────
// Requisition (hiring request from client)
// ─────────────────────────────────────────────

export type { RequisitionStatus, RequisitionPriority } from '@core/enums';

export interface Requisition {
  id: string;
  org_id: string;
  client_id: string;
  created_by: string;
  title: string;
  department: string | null;
  headcount: number;
  priority: RequisitionPriority;
  status: RequisitionStatus;
  hiring_manager_name: string | null;
  hiring_manager_email: string | null;
  budget_currency: string;
  budget_min: number | null;
  budget_max: number | null;
  location: string | null;
  employment_type: string | null;
  business_justification: string | null;
  must_have_skills: string[];
  nice_to_have_skills: string[];
  internal_notes: string | null;
  target_start_date: string | null;
  submitted_at: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  /** Phase 8 · C-37 — stamped by the BEFORE trigger on first transition into 'sourcing'. */
  opened_at: string | null;
  /** Phase 8 · C-37 — hard deadline for filling the position; defaulted from org TAT on open. */
  target_close_date: string | null;
  /** Phase 8 · C-37 — stamped on transition into 'filled' or 'cancelled'. */
  closed_at: string | null;
  /** REQ-094: first client outreach email queued (hiring-company gate). */
  client_outreach_sent_at?: string | null;
  /** Guided intake wizard: 1–5 while in progress; null when finished (migration 044). */
  intake_wizard_step?: number | null;
  /** REQ-010: the org_user assigned to approve this requisition */
  approver_user_id: string | null;
  approval_notes: string | null;
  /** REQ-030: FK to draft job auto-created on approve */
  created_job_id: string | null;
  job_id: string | null;
  created_at: string;
  updated_at: string;
  client?: Client;
  job?: Pick<Job, 'id' | 'title' | 'status' | 'description'>;
  /** Joined via approver_user_id → ggj_b2b_org_users (detail fetch). */
  approver_user?: Pick<OrgUser, 'id' | 'display_name' | 'email'> | null;
}

/** Row insert for ggj_b2b_requisitions (no joins). */
export type RequisitionInsert = Omit<Requisition, 'id' | 'created_at' | 'updated_at' | 'client' | 'job' | 'approver_user'>;

export type RequisitionClientEngagementKind =
  | 'email_to_client'
  | 'client_reply_recorded'
  | 'client_confirmation';

/** REQ-094: audit trail of hiring-client emails and recorded responses. */
export interface RequisitionClientEngagement {
  id: string;
  org_id: string;
  requisition_id: string;
  kind: RequisitionClientEngagementKind;
  channel: string;
  to_address: string | null;
  subject: string | null;
  body: string;
  created_by: string | null;
  created_at: string;
}

// ─────────────────────────────────────────────
// Application Types
// ─────────────────────────────────────────────

export type { ApplicationStatus } from '@core/enums';

export const REJECTION_REASON_CODES = [
  'not_qualified',
  'overqualified',
  'salary_mismatch',
  'no_show',
  'position_filled',
  'other',
] as const;
export type RejectionReasonCode = (typeof REJECTION_REASON_CODES)[number];

export const REJECTION_REASON_LABELS: Record<RejectionReasonCode, string> = {
  not_qualified:   'Not qualified',
  overqualified:   'Overqualified',
  salary_mismatch: 'Salary mismatch',
  no_show:         'No-show',
  position_filled: 'Position filled',
  other:           'Other',
};

export interface Application {
  id: string;
  org_id: string;
  candidate_id: string;
  job_id: string;
  client_id: string;
  assigned_to: string | null;
  status: ApplicationStatus;
  status_updated_at: string;
  status_updated_by: string | null;
  submitted_at: string;
  placement_date: string | null;
  placement_fee: number | null;
  internal_notes: string | null;
  /** PRD §11.1: internal offer / comp draft notes */
  offer_notes?: string | null;
  rejection_reason_code: RejectionReasonCode | null;
  rejection_reason_text: string | null;
  /** APP-060 / MAT-020: role-fit score (0–100) persisted from role-fit matrix */
  fitment_score: number | null;
  /** APP-060 / MAT-020: serialised gap analysis JSON from role-fit */
  fitment_json: Record<string, unknown> | null;
  /** Keycloak sub when the row was created from the B2B app (migration 085). */
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  /** §10.2 pipeline edit lease — holder’s ggj_b2b_org_users.id */
  pipeline_lock_org_user_id?: string | null;
  pipeline_lock_expires_at?: string | null;
  // joined
  candidate?: Candidate;
  job?: Job;
  client?: Client;
  assigned_user?: OrgUser;
  /** Populated when pipeline_lock_org_user_id is set (list/detail select). */
  pipeline_lock_holder?: Pick<OrgUser, 'id' | 'display_name' | 'email'> | null;
  /** Latest Single optimize / masking run for this application (0–1 rows) */
  optimization?: {
    ats_score_after: number | null;
    ats_score_before: number | null;
  } | null;
}

export interface ApplicationStatusHistory {
  id: string;
  application_id: string;
  from_status: ApplicationStatus | null;
  to_status: ApplicationStatus;
  changed_by: string | null;
  changed_at: string;
  note: string | null;
}

// ─────────────────────────────────────────────
// Email / CTA Types
// ─────────────────────────────────────────────

export type { CTAAction } from '@core/enums';

export interface EmailThread {
  id: string;
  org_id: string;
  application_id: string;
  sent_by: string | null;
  recipient_email: string;
  recipient_name: string | null;
  subject: string;
  body_html: string;
  template_key: string;
  sent_at: string | null;
  opened_at: string | null;
  open_count: number;
  cta_responded_at: string | null;
  cta_response: CTAAction | null;
  cta_response_note: string | null;
  in_reply_to_thread_id: string | null;
  track_token: string;
  cta_token: string;
  followup_count: number;
  next_followup_at: string | null;
  followup_stopped: boolean;
  created_at: string;
  /** Optional: provider bounce timestamp when column exists (delivery chip). */
  bounce_detected_at?: string | null;
  /** PUB-010 — CTA + track links invalid after this time (migration 024 + 031). */
  token_expires_at?: string | null;
  token_used_at?: string | null;
  token_max_uses?: number | null;
  token_use_count?: number | null;
  // joined
  application?: {
    id: string;
    status: string;
    candidate?: { id: string; full_name: string };
    job?: { id: string; title: string };
    client?: { id: string; name: string };
  };
}


// ─────────────────────────────────────────────
// Masking Types
// ─────────────────────────────────────────────

export interface MaskingTemplate {
  id: string;
  org_id: string;
  name: string;
  is_default: boolean;
  mask_full_name: boolean;
  mask_email: boolean;
  mask_phone: boolean;
  mask_linkedin_url: boolean;
  mask_current_company: boolean;
  mask_location: boolean;
  name_replacement: string;
  watermark_visible: boolean;
  watermark_text: string;
  created_at: string;
}

export interface CandidateAlias {
  id: string;
  org_id: string;
  candidate_id: string;
  client_id: string;
  alias: string;
  created_at: string;
}

// ─────────────────────────────────────────────
// Optimization Types
// ─────────────────────────────────────────────

export interface Optimization {
  id: string;
  org_id: string;
  application_id: string;
  candidate_id: string;
  job_id: string;
  optimized_resume_url: string | null;
  masked_resume_url: string | null;
  watermark_token: string | null;
  ats_score_before: number | null;
  ats_score_after: number | null;
  keyword_gaps: string[] | null;
  cover_letter_url: string | null;
  masking_template_id: string | null;
  masked_fields: Record<string, boolean> | null;
  credits_consumed: number;
  created_by: string;
  created_at: string;
}

/** §9.10.2 RoleFit AI — persisted projection per candidate × job (`ggj_b2b_rolefit_projections`). */
export type RoleFitTopology = '1:1' | '1:N' | 'N:1' | 'N:N';

export interface RoleFitProjection {
  id: string;
  org_id: string;
  candidate_id: string;
  job_id: string;
  topology: RoleFitTopology;
  fitment_score: number;
  coverage_score: number;
  ats_overall: number;
  ats_rating: string;
  projected_match_min: number | null;
  projected_match_max: number | null;
  match_probability: number | null;
  analysis_json: Record<string, unknown>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  candidate?: { id: string; full_name: string; email: string | null };
  job?: { id: string; title: string; client_id: string; client?: { id: string; name: string } | null };
}

// ─────────────────────────────────────────────
// Credit / Billing Types
// ─────────────────────────────────────────────

export type CreditType =
  | 'purchase'
  | 'consumption'
  | 'bonus'
  | 'refund'
  | 'referral_reward';

export interface CreditLedgerEntry {
  id: string;
  org_id: string;
  type: CreditType;
  amount: number;
  balance_after: number;
  reference_id: string | null;
  description: string | null;
  created_by: string | null;
  created_at: string;
}

// ─────────────────────────────────────────────
// Dashboard / Report Types
// ─────────────────────────────────────────────

export interface DashboardStats {
  submitted_count: number;
  shortlisted_count: number;
  placed_count: number;
  pending_queries_count: number;
  no_show_count: number;
  credits_remaining: number;
  submitted_delta: number;
  placed_delta: number;
}

export interface PipelineFunnelData {
  submitted: number;
  viewed: number;
  shortlisted: number;
  interviewed: number;
  offers: number;
  placed: number;
  rejected: number;
  no_shows: number;
  on_hold: number;
}

// ─────────────────────────────────────────────
// UI / Utility Types
// ─────────────────────────────────────────────

export interface SelectOption {
  value: string;
  label: string;
}

export interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
}

export type SortDirection = 'asc' | 'desc';
